<template>
  <div>
    <v-card elevation="0">
      <v-toolbar flat>
        <v-toolbar-title class="info--text font-weight-bold text--darken-1 justify-center">
          <span>{{ showUpcomingVisits ? "Today's" : "Past" }} Visits</span>
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <div>
          <v-select 
            v-model="selectedHeaders" 
            :items="visitHeaders" 
            label="Column Visibility" 
            multiple 
            outlined 
            return-object
            dense
            class="mt-6"
            @change="toggleColumnVisibility"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip v-if="index < 2">
                <span>{{ item.text }}</span>
              </v-chip>
              <span v-if="index === 2" class="grey--text caption">(+{{ selectedHeaders.length - 2 }} others)</span>
            </template>
         </v-select>
        </div>

        <v-tooltip left>
          <template v-slot:activator="{ on, attrs }">
            <v-hover v-slot="{ hover }">
              <v-btn :class="{ 'green--text': hover }" icon v-bind="attrs" v-on="on" @click="showUpcomingVisit">
                <v-icon color="green">mdi-sort-calendar-descending</v-icon>
              </v-btn>
            </v-hover>
          </template>
          <span class="text-caption">Upcoming Visits</span>
        </v-tooltip>

        <v-tooltip left>
          <template v-slot:activator="{ on, attrs }">
            <v-hover v-slot="{ hover }">
              <v-btn :class="{ 'red--text': hover }" icon v-bind="attrs" v-on="on" @click="showPastVisit">
                <v-icon color="red">mdi-sort-calendar-ascending</v-icon>
              </v-btn>
            </v-hover>
          </template>
          <span class="text-caption">Past Visits</span>
        </v-tooltip>

        <v-tooltip left>
          <template v-slot:activator="{ on, attrs }">
            <v-hover v-slot="{ hover }">
              <v-btn :class="{ 'info--text': hover }" icon v-bind="attrs" v-on="on" @click="scheduleModal">
                <v-icon color="info">mdi-calendar-plus</v-icon>
              </v-btn>
            </v-hover>
          </template>
          <span class="text-caption">Add Visit</span>
        </v-tooltip>

      </v-toolbar>

      <v-divider></v-divider>

      <v-data-table
        :headers="computedHeaders" 
        :items="visits" 
        sort-by="name" 
        class="elevation-1"
        loading-text="Loading... Please wait" 
        :search="filterReportBySearch" 
        show-select 
        :single-select="singleSelect"
        v-model="selected"
        :server-items-length="visitsPagination?.total"
        :options.sync="options"
      >
        <template v-slot:top>
          <v-progress-linear indeterminate color="info" v-if="loading"></v-progress-linear>
          <v-divider></v-divider>
        </template>

        <template v-slot:[`item.id`]="{ index }">
          {{ ++index }}
        </template>

        <template v-slot:[`item.event_date`]="{ item }">
          {{ formatDate(item.event_date) }}
        </template>

        <template v-slot:[`item.time`]="{ item }">
          {{ item.start_at }} - {{ item.end_at }}
        </template>

        <template v-slot:[`item.title`]="{ item }">
          <a
            @click.stop="visitPreview(item)"
            class="info--text text--darken-1 text-decoration-underline"
            >{{ item.title }}</a
          >
        </template>
        
        <template v-slot:[`item.status`]="{ item }">
          <div v-if="item.workflow?.approval_groups.length > 0">
            <div v-if="item.workflow?.approval_groups">
              <div v-if="item.receiving == 0">
                <a class="grey--text"> Closed </a>
              </div>

              <div
                v-else-if="item.active == 0"
              >
                <a class="warning--text"> Rejected </a>
              </div>

              <div v-else-if="item.active == 1">
                <a
                  class="info--text"
                  v-if="item.workflow?.approval_groups.length > item.status"
                  @click.stop="visitPreview(item)"
                >
                  Pending
                  <span
                    class="error--text"
                    v-if="item.workflow?.approval_groups.length > item.status"
                    >{{ item.workflow?.approval_groups[item.status]?.title }}</span
                  >
                  Approval
                </a>
                <a
                  class="success--text"
                  v-else-if="
                    item.workflow.approval_groups.length == item.status && item.active == 1
                  "
                >
                  Approved
                </a>
                <a class="error--text" v-else> Undefined </a>
              </div>

              <div v-else-if="item.active == 2">
                <a class="info--text">
                  Pending <span class="error--text">HOD</span> Approval
                </a>
              </div>

              <div v-else>
                <a class="error--text"> Undefined </a>
              </div>
            </div>
          </div>

          <div v-else>
            <router-link class="red--text text-decoration-none" link :to="href">
              Please Select Workflow Steps
            </router-link>
          </div>
        </template>

        <template v-slot:[`item.workflow`]="{ item }">
          <div v-if="item.workflow.approval_groups.length > 0">
            <a
              v-for="(wkfl, i) in item.workflow.approval_groups"
              :key="i"
              class="success--text"
            >
              <span v-if="i != 0"> <v-icon>mdi-arrow-right-bold</v-icon> </span>
              <a
                v-if="i == item.status && item.active == 1"
                class="success--text"
                >{{ wkfl.title }}</a
              >
              <a v-else class="primary--text">{{ wkfl.title }}</a>
            </a>
          </div>

          <div v-else>
            <router-link class="red--text text-decoration-none" link :to="href">
              Please Select Workflow Steps
            </router-link>
          </div>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <template>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <span class="d-inline-block mb-1" v-bind="attrs" v-on="on">
                  <v-btn
                    color="grey darken-1"
                    outlined
                    class="text-capitalize mx-1"
                    small
                    dark
                    @click="editItem(item)"
                  >
                    <v-icon small>mdi-eye</v-icon> Preview
                  </v-btn>
                </span>
              </template>
              <span>Preview</span>
            </v-tooltip>
          </template>
        </template>
      </v-data-table>
    </v-card>

    <!-- Schedule Visit Modal -->
    <ScheduleVisitModal 
      v-model="showScheduleModal"
      :organization-facilities="organizationFacilities"
      :approval-workflows="approvalWorkflows"
      :organization="organization"
      @get_upcoming_visits="getUpcomingVisits"
      @select_organization="selectOrganization"
      :selected-visit="selectedVisit"
    />
  </div>
</template>
<script>
import organizationService from '../../services/organization.service';
import ReportsService from '../../services/reports.service';
import moment from 'moment';
import ScheduleVisitModal from '../../components/ScheduleVisitModal.vue';
import WorkflowService from '../../services/workflow.service';

export default {
  name: "ScheduleVisit",

  components: {
    ScheduleVisitModal
  },

  props: [
    'organization'
  ],

  data() {
    return {
      visits: [],
      visitsPagination: {},
      selected: [],
      singleSelect: false,
      filterReportBySearch: null,
      options: {},
      loading: false,
      selectedHeaders: [],
      visitHeaders: [
        {
          text: "#",
          value: "id",
          visible: true,
        }, 
        {
          text: "Visitor",
          align: "start",
          filterable: true,
          value: "visitor.name",
          visible: true,
        },
        {
          text: "Host",
          value: "host.name",
          filterable: false,
          visible: true,
        },
        {
          text: "Title",
          value: "title",
          filterable: false,
          visible: false,
        },
        {
          text: "Approval Stages",
          value: "workflow",
          filterable: false,
          visible: true,
        },
        {
          text: "Approval Status",
          value: "status",
          filterable: false,
          visible: true,
        },
        {
          text: "Reason",
          value: "reason",
          filterable: false,
          visible: false,
        },
        {
          text: "Location",
          value: "facility",
          filterable: false,
          visible: true,
        },
        {
          text: "Date",
          align: "center",
          value: "event_date",
          filterable: false,
          visible: true,
        },
        {
          text: "Time",
          align: "center",
          value: "time",
          filterable: false,
          visible: true,
        },
        {
          text: "Visit Status",
          value: "attend_status",
          filterable: false,
          visible: true,
        },
      ],
      showUpcomingVisits: true,
      showScheduleModal: false,
      apiErrors: [],
      visitCategories: ["Official", "Personal"],
      closeOnContentClick: false,
      dateToday: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
      national_number: "KE",
      phoneNumber: null,
      phoneValidationError: null,
      organizationFacilities: [],
      approvalWorkflows: [],
      valid: false,
      selectedVisit: null,
    }
  },

  computed: {
    organizationFacility() {
      return this.organization.facility[0] ?? { 'id': null, 'facility': null };
    },

    visitsWithIndex() {
      if ((this.visits).length == 0) {
        return [];
      }

      return this.visits.map(
        (visits, index) => ({
          ...visits,
          index: index + 1
        })
      );
    },

    computedHeaders() {
      let headers = this.visitHeaders.filter((item) => (item.visible == true));
      return headers;
    },
  },

  created() {
    this.$nextTick(() => {
      this.selectedHeaders = this.computedHeaders;
      this.getUpcomingVisits();
      this.getPastVisits();
      this.getOrganizationFacilities();
      this.getApprovalWorkflows();
      this.showScheduleModal = false;
      this.selectedVisit = null;
    });
  },

  methods: {
    async selectOrganization(payload) {
      await this.$emit('select_organization', payload);
      this.getOrganizationFacilities();
      this.getApprovalWorkflows();
      this.getUpcomingVisits();
      this.getPastVisits();

      this.showScheduleModal = false;
      this.selectedVisit = null;
    },
    
    toggleColumnVisibility() {
      if (this.selectedHeaders && this.selectedHeaders.length > 0) {
        this.visitHeaders.forEach((visitHeader) => {
          let index = -1;
          index = this.visitHeaders.findIndex((header) => header.value == visitHeader.value);
          if (index > -1) {
            this.visitHeaders[index].visible = false;
          }
        });

        this.selectedHeaders.forEach((selectedHeader) => {
          let index = -1;
          index = this.visitHeaders.findIndex((header) => header.value == selectedHeader.value);
          if (index > -1) {
            this.visitHeaders[index].visible = true;
          }
        });
      }
    },

    scheduleModal() {
      this.showScheduleModal = true;
    },

    formatDate(date, format = 'DD-MMM-YYYY') {
      return moment(date).format(format);
    },

    toggleVisits() {
      let visits = [];
      let visitsPagination = {};
      
      if (this.showUpcomingVisits) {
        visitsPagination = this.upcomingVisits ?? {};
        visits = this.upcomingVisits?.data ?? [];
      } else {
        visitsPagination = this.pastVisits ?? {};
        visits = this.pastVisits?.data ?? [];
      }
      
      this.visits = visits;
      this.visitsPagination = visitsPagination;
    },

    showUpcomingVisit() {
      this.getUpcomingVisits();
      this.showUpcomingVisits = true;
      this.toggleVisits();
    },

    showPastVisit() {
      this.getPastVisits();
      this.showUpcomingVisits = false;
      this.toggleVisits();
    },

    getUpcomingVisits() {
      this.loading = true;
      ReportsService.upcomingVisits(this.organization.id, { options: this.options }).then(
        success => {
          this.upcomingVisits = success.data ? success.data.data : this.upcomingVisits
          this.toggleVisits();
          this.loading = false;
        },
        error => {
          console.log(error)
          this.upcomingVisits = [];
          this.loading = false;
        }
      )
    },

    getPastVisits() {
      this.loading = true;
      ReportsService.pastVisits(this.organization.id, { options: this.options }).then(
        success => {
          this.pastVisits = success.data.data;
          this.toggleVisits();
          this.loading = false;
        },
        error => {
          console.log(error)
          this.pastVisits = [];
          this.loading = false;
        }
      )
    },

    getOrganizationFacilities() {
      organizationService.organization_facilities(this.organization.id).then(
        success => {
          this.organizationFacilities = success.data ? success.data.data : [];
        },
        error => {
          console.log(error)
          this.organizationFacilities = [];
        }
      )
    },

    getApprovalWorkflows() {
      WorkflowService.getApprovalWorkflows(this.organization.id).then(
        success => {
          this.approvalWorkflows = success.data ? success.data?.data?.data : [];
        },
        error => {
          console.log(error)
          this.approvalWorkflows = [];
        }
      )
    },

    visitPreview(item) {
      this.$nextTick(() => {
        this.selectedVisit = Object.assign({}, item);
        this.scheduleModal();
      });
    },
  },
  watch: {
    options: {
      handler () {
        if (this.showUpcomingVisits) {
          this.getUpcomingVisits();
        } else {
          this.getPastVisits();
        }
      },
      deep: true,
    },
  },
}
</script>